import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { logInstagramShare } from '../../../services/analyticsService';

const DailyMessageContent = ({ message, onShare }) => {
  const [hasShared, setHasShared] = useState(false);
  const [showInstruction, setShowInstruction] = useState(false);

  const generateStoryImage = (text) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1920;
      const ctx = canvas.getContext('2d');

      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(0.5, '#16213e');
      gradient.addColorStop(1, '#0f3460');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Decorative circles
      ctx.beginPath();
      ctx.arc(900, 200, 300, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(229, 57, 53, 0.12)';
      ctx.fill();

      ctx.beginPath();
      ctx.arc(150, 1700, 250, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(229, 57, 53, 0.08)';
      ctx.fill();

      // Top logo area
      const logoSize = 90;
      const logoX = canvas.width / 2 - logoSize / 2;
      const logoY = 280;

      ctx.beginPath();
      ctx.roundRect(logoX, logoY, logoSize, logoSize, 20);
      ctx.fillStyle = 'rgba(229, 57, 53, 0.9)';
      ctx.fill();

      // Car icon (simplified)
      ctx.fillStyle = 'white';
      ctx.font = 'bold 52px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('🚗', canvas.width / 2, logoY + 65);

      // Brand name
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = 'bold 38px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Trafikkonya', canvas.width / 2, logoY + 140);

      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '28px Arial';
      ctx.fillText('Günlük Etkileşim Platformu', canvas.width / 2, logoY + 185);

      // Divider line
      ctx.beginPath();
      ctx.moveTo(200, 560);
      ctx.lineTo(880, 560);
      ctx.strokeStyle = 'rgba(229, 57, 53, 0.6)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // "Günün Mesajı" label
      ctx.fillStyle = 'rgba(229, 57, 53, 1)';
      ctx.font = 'bold 36px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('✦  GÜNÜN MESAJI  ✦', canvas.width / 2, 630);

      // Message text box
      const boxX = 80;
      const boxY = 680;
      const boxW = canvas.width - 160;
      const boxH = 760;
      ctx.beginPath();
      ctx.roundRect(boxX, boxY, boxW, boxH, 24);
      ctx.fillStyle = 'rgba(255,255,255,0.07)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Quote marks
      ctx.fillStyle = 'rgba(229, 57, 53, 0.5)';
      ctx.font = 'bold 120px Georgia';
      ctx.textAlign = 'left';
      ctx.fillText('"', boxX + 40, boxY + 110);

      // Wrap and draw message text
      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      ctx.font = '44px Arial';
      ctx.textAlign = 'center';
      const maxWidth = boxW - 100;
      const lineHeight = 68;
      const words = text.split(' ');
      const lines = [];
      let currentLine = '';

      for (const word of words) {
        const testLine = currentLine ? currentLine + ' ' + word : word;
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) lines.push(currentLine);

      const totalTextHeight = lines.length * lineHeight;
      const textStartY = boxY + (boxH - totalTextHeight) / 2 + lineHeight / 2;

      lines.forEach((line, i) => {
        ctx.fillText(line, canvas.width / 2, textStartY + i * lineHeight);
      });

      // Bottom divider
      ctx.beginPath();
      ctx.moveTo(200, 1520);
      ctx.lineTo(880, 1520);
      ctx.strokeStyle = 'rgba(229, 57, 53, 0.4)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Footer
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.font = '28px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('arackokusu.online', canvas.width / 2, 1580);

      resolve(canvas);
    });
  };

  const handleShare = async () => {
    const text = message || 'Trafik kurallarına uymak, sadece bir sorumluluk değil, hayat kurtaran bir alışkanlıktır. Bugün güvenli sürüş için bir adım at!';

    try {
      const canvas = await generateStoryImage(text);

      // Try to share image via clipboard (modern browsers)
      let imageShared = false;
      try {
        canvas.toBlob(async (blob) => {
          try {
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob })
            ]);
          } catch {
            // Clipboard image write not supported, fallback to text
            await navigator.clipboard.writeText(text).catch(() => {});
          }
        }, 'image/png');
        imageShared = true;
      } catch {
        // Fallback: copy text
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          const el = document.createElement('textarea');
          el.value = text;
          el.style.position = 'fixed';
          el.style.opacity = '0';
          document.body.appendChild(el);
          el.select();
          document.execCommand('copy');
          document.body.removeChild(el);
        }
      }

      // Also try Web Share API with image file for mobile
      if (navigator.share && navigator.canShare) {
        canvas.toBlob(async (blob) => {
          const file = new File([blob], 'gunun-mesaji.png', { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({
                files: [file],
                title: 'Günün Mesajı',
              });
              return;
            } catch {
              // User cancelled or not supported, continue with deep link
            }
          }
          // Deep-link to Instagram Stories camera
          setTimeout(() => {
            window.location.href = 'instagram://story-camera';
          }, 300);
        }, 'image/png');
      } else {
        setTimeout(() => {
          window.location.href = 'instagram://story-camera';
        }, 300);
      }

    } catch (err) {
      console.error('Share error:', err);
    }

    setHasShared(true);
    setShowInstruction(true);

    // Log share action to analytics
    await logInstagramShare({
      contentType: 'dailyMessage',
      instagramUsername: null
    });

    if (onShare) {
      onShare();
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary/10">
          <Icon name="MessageCircle" size={24} color="var(--color-primary)" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground mb-3 md:mb-4">
            Günün Mesajı
          </h3>
          <p className="text-base md:text-lg lg:text-xl text-foreground leading-relaxed">
            {message || 'Trafik kurallarına uymak, sadece bir sorumluluk değil, hayat kurtaran bir alışkanlıktır. Bugün güvenli sürüş için bir adım at!'}
          </p>
        </div>
      </div>

      {showInstruction && (
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <Icon name="Info" size={20} color="#3b82f6" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800 leading-relaxed">
            <span className="font-semibold">Hikaye görseli hazırlandı!</span> Instagram Stories kamerası açılıyor. Hikayenize bir fotoğraf/video ekleyip <span className="font-semibold">yapıştır (uzun bas → Yapıştır)</span> seçeneğiyle görseli ekleyebilirsiniz.
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <Button
          variant={hasShared ? 'success' : 'default'}
          size="lg"
          fullWidth
          onClick={handleShare}
          disabled={hasShared}
          iconName={hasShared ? 'CheckCircle' : 'Share2'}
          iconPosition="left"
        >
          {hasShared ? 'Paylaşıldı' : 'Instagram Stories\'e Paylaş'}
        </Button>
      </div>
    </div>
  );
};

export default DailyMessageContent;